import { numberToBinUint16LE, numberToBinUint32LE, } from '../../../format/format';
import { range } from '../../../format/hex';
import { pushToStack } from './combinators';
import { applyError, AuthenticationErrorCommon } from './errors';
import { OpcodesCommon } from './opcodes';
import { bigIntToScriptNumber } from './types';
export var PushOperationConstants;
(function (PushOperationConstants) {
    PushOperationConstants[PushOperationConstants["OP_0"] = 0] = "OP_0";
    /**
     * OP_PUSHBYTES_75
     */
    PushOperationConstants[PushOperationConstants["maximumPushByteOperationSize"] = 75] = "maximumPushByteOperationSize";
    PushOperationConstants[PushOperationConstants["OP_PUSHDATA_1"] = 76] = "OP_PUSHDATA_1";
    PushOperationConstants[PushOperationConstants["OP_PUSHDATA_2"] = 77] = "OP_PUSHDATA_2";
    PushOperationConstants[PushOperationConstants["OP_PUSHDATA_4"] = 78] = "OP_PUSHDATA_4";
    /**
     * OP_PUSHDATA_4
     */
    PushOperationConstants[PushOperationConstants["highestPushDataOpcode"] = 78] = "highestPushDataOpcode";
    /**
     * For OP_1 to OP_16, `opcode` is the number offset by `0x50` (80):
     *
     * `OP_N = 0x50 + N`
     *
     * OP_0 is really OP_PUSHBYTES_0 (`0x00`), so it does not follow this pattern.
     */
    PushOperationConstants[PushOperationConstants["pushNumberOpcodesOffset"] = 80] = "pushNumberOpcodesOffset";
    /** OP_1 through OP_16 */
    PushOperationConstants[PushOperationConstants["pushNumberOpcodes"] = 16] = "pushNumberOpcodes";
    PushOperationConstants[PushOperationConstants["negativeOne"] = 129] = "negativeOne";
    PushOperationConstants[PushOperationConstants["OP_1NEGATE"] = 79] = "OP_1NEGATE";
    /**
     * 256 - 1
     */
    PushOperationConstants[PushOperationConstants["maximumPushData1Size"] = 255] = "maximumPushData1Size";
    /**
     * Standard consensus parameter for most Bitcoin forks.
     */
    PushOperationConstants[PushOperationConstants["maximumPushSize"] = 520] = "maximumPushSize";
    /**
     * 256 ** 2 - 1
     */
    PushOperationConstants[PushOperationConstants["maximumPushData2Size"] = 65535] = "maximumPushData2Size";
    /**
     * 256 ** 4 - 1
     */
    PushOperationConstants[PushOperationConstants["maximumPushData4Size"] = 4294967295] = "maximumPushData4Size";
})(PushOperationConstants || (PushOperationConstants = {}));
/**
 * Returns the minimal bytecode required to push the provided `data` to the
 * stack.
 *
 * @remarks
 * This method conservatively encodes a `Uint8Array` as a data push. For Script
 * Numbers which can be pushed using a single opcode (-1 through 16), the
 * equivalent bytecode value is returned. Other `data` values will be prefixed
 * with the proper opcode and push length bytes (if necessary) to create the
 * minimal push instruction.
 *
 * Note, while some single-byte Script Number pushes will be minimally-encoded
 * by this method, all larger inputs will be encoded as-is (it cannot be assumed
 * that inputs are intended to be used as Script Numbers). To encode the push of
 * a Script Number, minimally-encode the number before passing it to this
 * method, e.g.:
 * `encodeDataPush(bigIntToScriptNumber(parseBytesAsScriptNumber(nonMinimalNumber)))`.
 *
 * The maximum `bytecode` length which can be encoded for a push in the Bitcoin
 * system is `4294967295` (~4GB). This method assumes a smaller input – if
 * `bytecode` has the potential to be longer, it should be checked (and the
 * error handled) prior to calling this method.
 *
 * @param data - the Uint8Array to push to the stack
 */
// eslint-disable-next-line complexity
export const encodeDataPush = (data) => data.length <= PushOperationConstants.maximumPushByteOperationSize
    ? data.length === 0
        ? Uint8Array.of(0)
        : data.length === 1
            ? data[0] !== 0 && data[0] <= PushOperationConstants.pushNumberOpcodes
                ? Uint8Array.of(data[0] + PushOperationConstants.pushNumberOpcodesOffset)
                : data[0] === PushOperationConstants.negativeOne
                    ? Uint8Array.of(PushOperationConstants.OP_1NEGATE)
                    : Uint8Array.from([1, ...data])
            : Uint8Array.from([data.length, ...data])
    : data.length <= PushOperationConstants.maximumPushData1Size
        ? Uint8Array.from([
            PushOperationConstants.OP_PUSHDATA_1,
            data.length,
            ...data,
        ])
        : data.length <= PushOperationConstants.maximumPushData2Size
            ? Uint8Array.from([
                PushOperationConstants.OP_PUSHDATA_2,
                ...numberToBinUint16LE(data.length),
                ...data,
            ])
            : Uint8Array.from([
                PushOperationConstants.OP_PUSHDATA_4,
                ...numberToBinUint32LE(data.length),
                ...data,
            ]);
/**
 * Returns true if the provided `data` is minimally-encoded by the provided
 * `opcode`.
 * @param opcode - the opcode used to push `data`
 * @param data - the contents of the push
 */
// eslint-disable-next-line complexity
export const isMinimalDataPush = (opcode, data) => data.length === 0
    ? opcode === PushOperationConstants.OP_0
    : data.length === 1
        ? data[0] >= 1 && data[0] <= PushOperationConstants.pushNumberOpcodes
            ? opcode === data[0] + PushOperationConstants.pushNumberOpcodesOffset
            : data[0] === PushOperationConstants.negativeOne
                ? opcode === PushOperationConstants.OP_1NEGATE
                : true
        : data.length <= PushOperationConstants.maximumPushByteOperationSize
            ? opcode === data.length
            : data.length <= PushOperationConstants.maximumPushData1Size
                ? opcode === PushOperationConstants.OP_PUSHDATA_1
                : data.length <= PushOperationConstants.maximumPushData2Size
                    ? opcode === PushOperationConstants.OP_PUSHDATA_2
                    : true;
export const pushByteOpcodes = [
    OpcodesCommon.OP_PUSHBYTES_1,
    OpcodesCommon.OP_PUSHBYTES_2,
    OpcodesCommon.OP_PUSHBYTES_3,
    OpcodesCommon.OP_PUSHBYTES_4,
    OpcodesCommon.OP_PUSHBYTES_5,
    OpcodesCommon.OP_PUSHBYTES_6,
    OpcodesCommon.OP_PUSHBYTES_7,
    OpcodesCommon.OP_PUSHBYTES_8,
    OpcodesCommon.OP_PUSHBYTES_9,
    OpcodesCommon.OP_PUSHBYTES_10,
    OpcodesCommon.OP_PUSHBYTES_11,
    OpcodesCommon.OP_PUSHBYTES_12,
    OpcodesCommon.OP_PUSHBYTES_13,
    OpcodesCommon.OP_PUSHBYTES_14,
    OpcodesCommon.OP_PUSHBYTES_15,
    OpcodesCommon.OP_PUSHBYTES_16,
    OpcodesCommon.OP_PUSHBYTES_17,
    OpcodesCommon.OP_PUSHBYTES_18,
    OpcodesCommon.OP_PUSHBYTES_19,
    OpcodesCommon.OP_PUSHBYTES_20,
    OpcodesCommon.OP_PUSHBYTES_21,
    OpcodesCommon.OP_PUSHBYTES_22,
    OpcodesCommon.OP_PUSHBYTES_23,
    OpcodesCommon.OP_PUSHBYTES_24,
    OpcodesCommon.OP_PUSHBYTES_25,
    OpcodesCommon.OP_PUSHBYTES_26,
    OpcodesCommon.OP_PUSHBYTES_27,
    OpcodesCommon.OP_PUSHBYTES_28,
    OpcodesCommon.OP_PUSHBYTES_29,
    OpcodesCommon.OP_PUSHBYTES_30,
    OpcodesCommon.OP_PUSHBYTES_31,
    OpcodesCommon.OP_PUSHBYTES_32,
    OpcodesCommon.OP_PUSHBYTES_33,
    OpcodesCommon.OP_PUSHBYTES_34,
    OpcodesCommon.OP_PUSHBYTES_35,
    OpcodesCommon.OP_PUSHBYTES_36,
    OpcodesCommon.OP_PUSHBYTES_37,
    OpcodesCommon.OP_PUSHBYTES_38,
    OpcodesCommon.OP_PUSHBYTES_39,
    OpcodesCommon.OP_PUSHBYTES_40,
    OpcodesCommon.OP_PUSHBYTES_41,
    OpcodesCommon.OP_PUSHBYTES_42,
    OpcodesCommon.OP_PUSHBYTES_43,
    OpcodesCommon.OP_PUSHBYTES_44,
    OpcodesCommon.OP_PUSHBYTES_45,
    OpcodesCommon.OP_PUSHBYTES_46,
    OpcodesCommon.OP_PUSHBYTES_47,
    OpcodesCommon.OP_PUSHBYTES_48,
    OpcodesCommon.OP_PUSHBYTES_49,
    OpcodesCommon.OP_PUSHBYTES_50,
    OpcodesCommon.OP_PUSHBYTES_51,
    OpcodesCommon.OP_PUSHBYTES_52,
    OpcodesCommon.OP_PUSHBYTES_53,
    OpcodesCommon.OP_PUSHBYTES_54,
    OpcodesCommon.OP_PUSHBYTES_55,
    OpcodesCommon.OP_PUSHBYTES_56,
    OpcodesCommon.OP_PUSHBYTES_57,
    OpcodesCommon.OP_PUSHBYTES_58,
    OpcodesCommon.OP_PUSHBYTES_59,
    OpcodesCommon.OP_PUSHBYTES_60,
    OpcodesCommon.OP_PUSHBYTES_61,
    OpcodesCommon.OP_PUSHBYTES_62,
    OpcodesCommon.OP_PUSHBYTES_63,
    OpcodesCommon.OP_PUSHBYTES_64,
    OpcodesCommon.OP_PUSHBYTES_65,
    OpcodesCommon.OP_PUSHBYTES_66,
    OpcodesCommon.OP_PUSHBYTES_67,
    OpcodesCommon.OP_PUSHBYTES_68,
    OpcodesCommon.OP_PUSHBYTES_69,
    OpcodesCommon.OP_PUSHBYTES_70,
    OpcodesCommon.OP_PUSHBYTES_71,
    OpcodesCommon.OP_PUSHBYTES_72,
    OpcodesCommon.OP_PUSHBYTES_73,
    OpcodesCommon.OP_PUSHBYTES_74,
    OpcodesCommon.OP_PUSHBYTES_75,
];
const executionIsActive = (state) => state.executionStack.every((item) => item);
export const pushOperation = (flags, maximumPushSize = PushOperationConstants.maximumPushSize) => (state) => {
    const instruction = state.instructions[state.ip];
    return instruction.data.length > maximumPushSize
        ? applyError(AuthenticationErrorCommon.exceedsMaximumPush, state)
        : executionIsActive(state)
            ? flags.requireMinimalEncoding &&
                !isMinimalDataPush(instruction.opcode, instruction.data)
                ? applyError(AuthenticationErrorCommon.nonMinimalPush, state)
                : pushToStack(state, instruction.data)
            : state;
};
export const pushOperations = (flags, maximumPushSize = PushOperationConstants.maximumPushSize) => {
    const push = pushOperation(flags, maximumPushSize);
    return range(PushOperationConstants.highestPushDataOpcode + 1).reduce((group, i) => ({ ...group, [i]: push }), {});
};
export const pushNumberOpcodes = [
    OpcodesCommon.OP_1NEGATE,
    OpcodesCommon.OP_1,
    OpcodesCommon.OP_2,
    OpcodesCommon.OP_3,
    OpcodesCommon.OP_4,
    OpcodesCommon.OP_5,
    OpcodesCommon.OP_6,
    OpcodesCommon.OP_7,
    OpcodesCommon.OP_8,
    OpcodesCommon.OP_9,
    OpcodesCommon.OP_10,
    OpcodesCommon.OP_11,
    OpcodesCommon.OP_12,
    OpcodesCommon.OP_13,
    OpcodesCommon.OP_14,
    OpcodesCommon.OP_15,
    OpcodesCommon.OP_16,
];
const op1NegateValue = -1;
export const pushNumberOperations = () => pushNumberOpcodes
    .map((opcode, i) => [
    opcode,
    [op1NegateValue, ...range(PushOperationConstants.pushNumberOpcodes, 1)]
        .map(BigInt)
        .map(bigIntToScriptNumber)[i],
])
    .reduce((group, pair) => ({
    ...group,
    [pair[0]]: (state) => pushToStack(state, pair[1].slice()),
}), {});
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoicHVzaC5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uLy4uLy4uLy4uLy4uLy4uL3NyYy9saWIvYXV0aC9pbnN0cnVjdGlvbi1zZXRzL2NvbW1vbi9wdXNoLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBLE9BQU8sRUFDTCxtQkFBbUIsRUFDbkIsbUJBQW1CLEdBQ3BCLE1BQU0sd0JBQXdCLENBQUM7QUFDaEMsT0FBTyxFQUFFLEtBQUssRUFBRSxNQUFNLHFCQUFxQixDQUFDO0FBVTVDLE9BQU8sRUFBRSxXQUFXLEVBQUUsTUFBTSxlQUFlLENBQUM7QUFDNUMsT0FBTyxFQUFFLFVBQVUsRUFBRSx5QkFBeUIsRUFBRSxNQUFNLFVBQVUsQ0FBQztBQUNqRSxPQUFPLEVBQUUsYUFBYSxFQUFFLE1BQU0sV0FBVyxDQUFDO0FBQzFDLE9BQU8sRUFBRSxvQkFBb0IsRUFBRSxNQUFNLFNBQVMsQ0FBQztBQUUvQyxNQUFNLENBQU4sSUFBWSxzQkF5Q1g7QUF6Q0QsV0FBWSxzQkFBc0I7SUFDaEMsbUVBQVEsQ0FBQTtJQUNSOztPQUVHO0lBQ0gsb0hBQW1DLENBQUE7SUFDbkMsc0ZBQW9CLENBQUE7SUFDcEIsc0ZBQW9CLENBQUE7SUFDcEIsc0ZBQW9CLENBQUE7SUFDcEI7O09BRUc7SUFDSCxzR0FBcUMsQ0FBQTtJQUNyQzs7Ozs7O09BTUc7SUFDSCwwR0FBOEIsQ0FBQTtJQUM5Qix5QkFBeUI7SUFDekIsOEZBQXNCLENBQUE7SUFDdEIsbUZBQWtCLENBQUE7SUFDbEIsZ0ZBQWUsQ0FBQTtJQUNmOztPQUVHO0lBQ0gscUdBQTBCLENBQUE7SUFDMUI7O09BRUc7SUFDSCwyRkFBcUIsQ0FBQTtJQUNyQjs7T0FFRztJQUNILHVHQUE0QixDQUFBO0lBQzVCOztPQUVHO0lBQ0gsNEdBQWlDLENBQUE7QUFDbkMsQ0FBQyxFQXpDVyxzQkFBc0IsS0FBdEIsc0JBQXNCLFFBeUNqQztBQUVEOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7R0F3Qkc7QUFDSCxzQ0FBc0M7QUFDdEMsTUFBTSxDQUFDLE1BQU0sY0FBYyxHQUFHLENBQUMsSUFBZ0IsRUFBRSxFQUFFLENBQ2pELElBQUksQ0FBQyxNQUFNLElBQUksc0JBQXNCLENBQUMsNEJBQTRCO0lBQ2hFLENBQUMsQ0FBQyxJQUFJLENBQUMsTUFBTSxLQUFLLENBQUM7UUFDakIsQ0FBQyxDQUFDLFVBQVUsQ0FBQyxFQUFFLENBQUMsQ0FBQyxDQUFDO1FBQ2xCLENBQUMsQ0FBQyxJQUFJLENBQUMsTUFBTSxLQUFLLENBQUM7WUFDbkIsQ0FBQyxDQUFDLElBQUksQ0FBQyxDQUFDLENBQUMsS0FBSyxDQUFDLElBQUksSUFBSSxDQUFDLENBQUMsQ0FBQyxJQUFJLHNCQUFzQixDQUFDLGlCQUFpQjtnQkFDcEUsQ0FBQyxDQUFDLFVBQVUsQ0FBQyxFQUFFLENBQ1gsSUFBSSxDQUFDLENBQUMsQ0FBQyxHQUFHLHNCQUFzQixDQUFDLHVCQUF1QixDQUN6RDtnQkFDSCxDQUFDLENBQUMsSUFBSSxDQUFDLENBQUMsQ0FBQyxLQUFLLHNCQUFzQixDQUFDLFdBQVc7b0JBQ2hELENBQUMsQ0FBQyxVQUFVLENBQUMsRUFBRSxDQUFDLHNCQUFzQixDQUFDLFVBQVUsQ0FBQztvQkFDbEQsQ0FBQyxDQUFDLFVBQVUsQ0FBQyxJQUFJLENBQUMsQ0FBQyxDQUFDLEVBQUUsR0FBRyxJQUFJLENBQUMsQ0FBQztZQUNqQyxDQUFDLENBQUMsVUFBVSxDQUFDLElBQUksQ0FBQyxDQUFDLElBQUksQ0FBQyxNQUFNLEVBQUUsR0FBRyxJQUFJLENBQUMsQ0FBQztJQUMzQyxDQUFDLENBQUMsSUFBSSxDQUFDLE1BQU0sSUFBSSxzQkFBc0IsQ0FBQyxvQkFBb0I7UUFDNUQsQ0FBQyxDQUFDLFVBQVUsQ0FBQyxJQUFJLENBQUM7WUFDZCxzQkFBc0IsQ0FBQyxhQUFhO1lBQ3BDLElBQUksQ0FBQyxNQUFNO1lBQ1gsR0FBRyxJQUFJO1NBQ1IsQ0FBQztRQUNKLENBQUMsQ0FBQyxJQUFJLENBQUMsTUFBTSxJQUFJLHNCQUFzQixDQUFDLG9CQUFvQjtZQUM1RCxDQUFDLENBQUMsVUFBVSxDQUFDLElBQUksQ0FBQztnQkFDZCxzQkFBc0IsQ0FBQyxhQUFhO2dCQUNwQyxHQUFHLG1CQUFtQixDQUFDLElBQUksQ0FBQyxNQUFNLENBQUM7Z0JBQ25DLEdBQUcsSUFBSTthQUNSLENBQUM7WUFDSixDQUFDLENBQUMsVUFBVSxDQUFDLElBQUksQ0FBQztnQkFDZCxzQkFBc0IsQ0FBQyxhQUFhO2dCQUNwQyxHQUFHLG1CQUFtQixDQUFDLElBQUksQ0FBQyxNQUFNLENBQUM7Z0JBQ25DLEdBQUcsSUFBSTthQUNSLENBQUMsQ0FBQztBQUVUOzs7OztHQUtHO0FBQ0gsc0NBQXNDO0FBQ3RDLE1BQU0sQ0FBQyxNQUFNLGlCQUFpQixHQUFHLENBQUMsTUFBYyxFQUFFLElBQWdCLEVBQUUsRUFBRSxDQUNwRSxJQUFJLENBQUMsTUFBTSxLQUFLLENBQUM7SUFDZixDQUFDLENBQUMsTUFBTSxLQUFLLHNCQUFzQixDQUFDLElBQUk7SUFDeEMsQ0FBQyxDQUFDLElBQUksQ0FBQyxNQUFNLEtBQUssQ0FBQztRQUNuQixDQUFDLENBQUMsSUFBSSxDQUFDLENBQUMsQ0FBQyxJQUFJLENBQUMsSUFBSSxJQUFJLENBQUMsQ0FBQyxDQUFDLElBQUksc0JBQXNCLENBQUMsaUJBQWlCO1lBQ25FLENBQUMsQ0FBQyxNQUFNLEtBQUssSUFBSSxDQUFDLENBQUMsQ0FBQyxHQUFHLHNCQUFzQixDQUFDLHVCQUF1QjtZQUNyRSxDQUFDLENBQUMsSUFBSSxDQUFDLENBQUMsQ0FBQyxLQUFLLHNCQUFzQixDQUFDLFdBQVc7Z0JBQ2hELENBQUMsQ0FBQyxNQUFNLEtBQUssc0JBQXNCLENBQUMsVUFBVTtnQkFDOUMsQ0FBQyxDQUFDLElBQUk7UUFDUixDQUFDLENBQUMsSUFBSSxDQUFDLE1BQU0sSUFBSSxzQkFBc0IsQ0FBQyw0QkFBNEI7WUFDcEUsQ0FBQyxDQUFDLE1BQU0sS0FBSyxJQUFJLENBQUMsTUFBTTtZQUN4QixDQUFDLENBQUMsSUFBSSxDQUFDLE1BQU0sSUFBSSxzQkFBc0IsQ0FBQyxvQkFBb0I7Z0JBQzVELENBQUMsQ0FBQyxNQUFNLEtBQUssc0JBQXNCLENBQUMsYUFBYTtnQkFDakQsQ0FBQyxDQUFDLElBQUksQ0FBQyxNQUFNLElBQUksc0JBQXNCLENBQUMsb0JBQW9CO29CQUM1RCxDQUFDLENBQUMsTUFBTSxLQUFLLHNCQUFzQixDQUFDLGFBQWE7b0JBQ2pELENBQUMsQ0FBQyxJQUFJLENBQUM7QUFFWCxNQUFNLENBQUMsTUFBTSxlQUFlLEdBQTZCO0lBQ3ZELGFBQWEsQ0FBQyxjQUFjO0lBQzVCLGFBQWEsQ0FBQyxjQUFjO0lBQzVCLGFBQWEsQ0FBQyxjQUFjO0lBQzVCLGFBQWEsQ0FBQyxjQUFjO0lBQzVCLGFBQWEsQ0FBQyxjQUFjO0lBQzVCLGFBQWEsQ0FBQyxjQUFjO0lBQzVCLGFBQWEsQ0FBQyxjQUFjO0lBQzVCLGFBQWEsQ0FBQyxjQUFjO0lBQzVCLGFBQWEsQ0FBQyxjQUFjO0lBQzVCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0lBQzdCLGFBQWEsQ0FBQyxlQUFlO0NBQzlCLENBQUM7QUFFRixNQUFNLGlCQUFpQixHQUFHLENBQW9DLEtBQVksRUFBRSxFQUFFLENBQzVFLEtBQUssQ0FBQyxjQUFjLENBQUMsS0FBSyxDQUFDLENBQUMsSUFBSSxFQUFFLEVBQUUsQ0FBQyxJQUFJLENBQUMsQ0FBQztBQUU3QyxNQUFNLENBQUMsTUFBTSxhQUFhLEdBQUcsQ0FRM0IsS0FBMEMsRUFDMUMsZUFBZSxHQUFHLHNCQUFzQixDQUFDLGVBQWUsRUFDdEMsRUFBRSxDQUFDLENBQUMsS0FBWSxFQUFFLEVBQUU7SUFDdEMsTUFBTSxXQUFXLEdBQUcsS0FBSyxDQUFDLFlBQVksQ0FDcEMsS0FBSyxDQUFDLEVBQUUsQ0FDaUMsQ0FBQztJQUM1QyxPQUFPLFdBQVcsQ0FBQyxJQUFJLENBQUMsTUFBTSxHQUFHLGVBQWU7UUFDOUMsQ0FBQyxDQUFDLFVBQVUsQ0FDUix5QkFBeUIsQ0FBQyxrQkFBa0IsRUFDNUMsS0FBSyxDQUNOO1FBQ0gsQ0FBQyxDQUFDLGlCQUFpQixDQUFDLEtBQUssQ0FBQztZQUMxQixDQUFDLENBQUMsS0FBSyxDQUFDLHNCQUFzQjtnQkFDNUIsQ0FBQyxpQkFBaUIsQ0FDZixXQUFXLENBQUMsTUFBNEIsRUFDekMsV0FBVyxDQUFDLElBQUksQ0FDakI7Z0JBQ0QsQ0FBQyxDQUFDLFVBQVUsQ0FDUix5QkFBeUIsQ0FBQyxjQUFjLEVBQ3hDLEtBQUssQ0FDTjtnQkFDSCxDQUFDLENBQUMsV0FBVyxDQUFDLEtBQUssRUFBRSxXQUFXLENBQUMsSUFBSSxDQUFDO1lBQ3hDLENBQUMsQ0FBQyxLQUFLLENBQUM7QUFDWixDQUFDLENBQUM7QUFFRixNQUFNLENBQUMsTUFBTSxjQUFjLEdBQUcsQ0FRNUIsS0FBMEMsRUFDMUMsZUFBZSxHQUFHLHNCQUFzQixDQUFDLGVBQWUsRUFDeEQsRUFBRTtJQUNGLE1BQU0sSUFBSSxHQUFHLGFBQWEsQ0FBeUIsS0FBSyxFQUFFLGVBQWUsQ0FBQyxDQUFDO0lBQzNFLE9BQU8sS0FBSyxDQUFDLHNCQUFzQixDQUFDLHFCQUFxQixHQUFHLENBQUMsQ0FBQyxDQUFDLE1BQU0sQ0FFbEUsQ0FBQyxLQUFLLEVBQUUsQ0FBQyxFQUFFLEVBQUUsQ0FBQyxDQUFDLEVBQUUsR0FBRyxLQUFLLEVBQUUsQ0FBQyxDQUFDLENBQUMsRUFBRSxJQUFJLEVBQUUsQ0FBQyxFQUFFLEVBQUUsQ0FBQyxDQUFDO0FBQ2xELENBQUMsQ0FBQztBQUVGLE1BQU0sQ0FBQyxNQUFNLGlCQUFpQixHQUE2QjtJQUN6RCxhQUFhLENBQUMsVUFBVTtJQUN4QixhQUFhLENBQUMsSUFBSTtJQUNsQixhQUFhLENBQUMsSUFBSTtJQUNsQixhQUFhLENBQUMsSUFBSTtJQUNsQixhQUFhLENBQUMsSUFBSTtJQUNsQixhQUFhLENBQUMsSUFBSTtJQUNsQixhQUFhLENBQUMsSUFBSTtJQUNsQixhQUFhLENBQUMsSUFBSTtJQUNsQixhQUFhLENBQUMsSUFBSTtJQUNsQixhQUFhLENBQUMsSUFBSTtJQUNsQixhQUFhLENBQUMsS0FBSztJQUNuQixhQUFhLENBQUMsS0FBSztJQUNuQixhQUFhLENBQUMsS0FBSztJQUNuQixhQUFhLENBQUMsS0FBSztJQUNuQixhQUFhLENBQUMsS0FBSztJQUNuQixhQUFhLENBQUMsS0FBSztJQUNuQixhQUFhLENBQUMsS0FBSztDQUNwQixDQUFDO0FBRUYsTUFBTSxjQUFjLEdBQUcsQ0FBQyxDQUFDLENBQUM7QUFFMUIsTUFBTSxDQUFDLE1BQU0sb0JBQW9CLEdBQUcsR0FHaEMsRUFBRSxDQUNKLGlCQUFpQjtLQUNkLEdBQUcsQ0FBOEIsQ0FBQyxNQUFNLEVBQUUsQ0FBQyxFQUFFLEVBQUUsQ0FBQztJQUMvQyxNQUFNO0lBQ04sQ0FBQyxjQUFjLEVBQUUsR0FBRyxLQUFLLENBQUMsc0JBQXNCLENBQUMsaUJBQWlCLEVBQUUsQ0FBQyxDQUFDLENBQUM7U0FDcEUsR0FBRyxDQUFDLE1BQU0sQ0FBQztTQUNYLEdBQUcsQ0FBQyxvQkFBb0IsQ0FBQyxDQUFDLENBQUMsQ0FBQztDQUNoQyxDQUFDO0tBQ0QsTUFBTSxDQUdMLENBQUMsS0FBSyxFQUFFLElBQUksRUFBRSxFQUFFLENBQUMsQ0FBQztJQUNoQixHQUFHLEtBQUs7SUFDUixDQUFDLElBQUksQ0FBQyxDQUFDLENBQUMsQ0FBQyxFQUFFLENBQUMsS0FBbUIsRUFBRSxFQUFFLENBQUMsV0FBVyxDQUFDLEtBQUssRUFBRSxJQUFJLENBQUMsQ0FBQyxDQUFDLENBQUMsS0FBSyxFQUFFLENBQUM7Q0FDeEUsQ0FBQyxFQUNGLEVBQUUsQ0FDSCxDQUFDIn0=